import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import {
  getKitchenDisplayDetails,
  updateKitchenDisplay,
  getCategoriesApi
} from '../Controller/KitchenDisplayController'
import Utility from '../../../../GlobalModules/Utility/Utility'
import '../Assets/css/kitchen-display.scss'

const EditKitchenDisplay = (props) => {
  const { lng, slack } = useParams()
  const history = useHistory()

  const [formData, setFormData] = useState({
    kitchen_display_code: '',
    label: '',
    status: '',
    orange_timer: '',
    red_timer: '',
    categories: []
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [availableCategories, setAvailableCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)

  // Prevent multiple API calls
  const [lastApiCall, setLastApiCall] = useState(null)

  // Fetch categories from API on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      // Prevent multiple simultaneous API calls
      const now = Date.now()
      if (lastApiCall && now - lastApiCall < 1000) {
        console.log('Preventing duplicate categories API call')
        return
      }

      setLastApiCall(now)

      try {
        setCategoriesLoading(true)
        const response = await getCategoriesApi()
        if (response.success) {
          setAvailableCategories(response.data)
          console.log('Available categories:', response.data)
        } else {
          console.error('Failed to fetch categories:', response.message)
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
      } finally {
        setCategoriesLoading(false)
      }
    }

    fetchCategories()
  }, [lastApiCall])

  useEffect(() => {
    fetchKitchenDisplayDetails()
  }, [slack])

  const fetchKitchenDisplayDetails = async () => {
    // Prevent multiple simultaneous API calls
    const now = Date.now()
    if (lastApiCall && now - lastApiCall < 1000) {
      console.log('Preventing duplicate kitchen display details API call')
      return
    }

    setLastApiCall(now)

    try {
      setInitialLoading(true)
      const response = await getKitchenDisplayDetails(slack)

      if (response.success) {
        const data = response.data
        setFormData({
          kitchen_display_code: data.kitchen_display_code || '',
          label: data.label || '',
          status: data.status?.toString() || '',
          orange_timer: data.orange_timer?.toString() || '',
          red_timer: data.red_timer?.toString() || '',
          categories: Array.isArray(data.categories)
            ? data.categories.map((category) => {
                // If category is an object with id, use the id
                if (typeof category === 'object' && category.id) {
                  return parseInt(category.id)
                }
                // If category is just a number/string, use it directly
                return parseInt(category)
              })
            : []
        })
      } else {
        setErrors({
          submit: 'Failed to load Kitchen Display details'
        })
      }
    } catch (err) {
      setErrors({
        submit: 'An error occurred while loading Kitchen Display details'
      })
    } finally {
      setInitialLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Kitchen Display Code validation
    if (!formData.kitchen_display_code.trim()) {
      newErrors.kitchen_display_code = 'Kitchen Display Code is required'
    } else {
      const code = formData.kitchen_display_code.trim()

      // Length validation
      if (code.length < 3) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must be at least 3 characters long'
      } else if (code.length > 50) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must not exceed 50 characters'
      }

      // Character validation - only alphanumeric, hyphens, and underscores allowed
      else if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code can only contain letters, numbers, hyphens, and underscores'
      }

      // Format validation - must start with a letter or number
      else if (!/^[a-zA-Z0-9]/.test(code)) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must start with a letter or number'
      }

      // Format validation - must end with a letter or number
      else if (!/[a-zA-Z0-9]$/.test(code)) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must end with a letter or number'
      }
    }

    // Name validation
    if (!formData.label.trim()) {
      newErrors.label = 'Name is required'
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required'
    }

    // Orange Timer validation
    if (!formData.orange_timer) {
      newErrors.orange_timer = 'Please enter value in minutes'
    } else if (
      isNaN(formData.orange_timer) ||
      parseFloat(formData.orange_timer) <= 0
    ) {
      newErrors.orange_timer = 'Please enter a valid number in minutes'
    }

    // Red Timer validation
    if (!formData.red_timer) {
      newErrors.red_timer = 'Please enter value in minutes'
    } else if (
      isNaN(formData.red_timer) ||
      parseFloat(formData.red_timer) <= 0
    ) {
      newErrors.red_timer = 'Please enter a valid number in minutes'
    }

    // Validate that Red Timer is greater than Orange Timer
    if (formData.orange_timer && formData.red_timer) {
      const orangeTimer = parseFloat(formData.orange_timer)
      const redTimer = parseFloat(formData.red_timer)
      if (redTimer <= orangeTimer) {
        newErrors.red_timer = 'Red Timer must be greater than Orange Timer'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateField = (name, value) => {
    if (name === 'kitchen_display_code') {
      if (!value.trim()) {
        return 'Kitchen Display Code is required'
      } else {
        const code = value.trim()

        // Length validation
        if (code.length < 3) {
          return 'Kitchen Display Code must be at least 3 characters long'
        } else if (code.length > 50) {
          return 'Kitchen Display Code must not exceed 50 characters'
        }

        // Character validation - only alphanumeric, hyphens, and underscores allowed
        else if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
          return 'Kitchen Display Code can only contain letters, numbers, hyphens, and underscores'
        }

        // Format validation - must start with a letter or number
        else if (!/^[a-zA-Z0-9]/.test(code)) {
          return 'Kitchen Display Code must start with a letter or number'
        }

        // Format validation - must end with a letter or number
        else if (!/[a-zA-Z0-9]$/.test(code)) {
          return 'Kitchen Display Code must end with a letter or number'
        }
      }
    }
    return ''
  }

  const clearError = (field) => {
    setErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    clearError(name)
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))

    // Real-time validation
    const fieldError = validateField(name, value)
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError
    }))
  }

  const handleCategoryChange = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      categories: Array.isArray(prev.categories)
        ? prev.categories.includes(categoryId)
          ? prev.categories.filter((id) => id !== categoryId)
          : [...prev.categories, categoryId]
        : [categoryId]
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const payload = {
        kitchen_display_code: formData.kitchen_display_code.trim(),
        label: formData.label.trim(),
        status: parseInt(formData.status),
        orange_timer: parseFloat(formData.orange_timer),
        red_timer: parseFloat(formData.red_timer),
        categories: Array.isArray(formData.categories)
          ? formData.categories
          : []
      }

      console.log('Sending payload:', payload) // Debug log

      const response = await updateKitchenDisplay(slack, payload)

      if (response.success) {
        Utility.toastNotifications(
          'Kitchen Display updated successfully!',
          'Success',
          'success'
        )
        setTimeout(() => {
          history.push(`/${lng}/settings/kitchen-displays`)
        }, 1500)
      } else {
        Utility.toastNotifications(
          response.message || 'Failed to update Kitchen Display',
          'Error',
          'error'
        )
      }
    } catch (err) {
      Utility.toastNotifications(
        'An error occurred while updating Kitchen Display',
        'Error',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <Container fluid className='p-0'>
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <Spinner animation='border' variant='primary' />
          <span className='ms-2'>Loading Kitchen Display details...</span>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Edit Kitchen Display</h4>
        <Button
          variant='outline-secondary'
          onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
          Back to List
        </Button>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className='mb-4 position-relative'>
                  <Form.Label>Kitchen Display Code *</Form.Label>
                  <Form.Control
                    type='text'
                    name='kitchen_display_code'
                    value={formData.kitchen_display_code}
                    onChange={handleInputChange}
                    placeholder='Please enter kitchen display code'
                    className={errors.kitchen_display_code ? 'is-invalid' : ''}
                  />
                  {errors.kitchen_display_code && (
                    <div
                      className='invalid-feedback d-block position-absolute'
                      style={{ top: '100%', zIndex: 1000 }}
                    >
                      {errors.kitchen_display_code}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-4 position-relative'>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type='text'
                    name='label'
                    value={formData.label}
                    onChange={handleInputChange}
                    placeholder='Please enter name'
                    className={errors.label ? 'is-invalid' : ''}
                  />
                  {errors.label && (
                    <div
                      className='invalid-feedback d-block position-absolute'
                      style={{ top: '100%', zIndex: 1000 }}
                    >
                      {errors.label}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className='mb-4 position-relative'>
                  <Form.Label>Status *</Form.Label>
                  <Form.Select
                    name='status'
                    value={formData.status}
                    onChange={handleInputChange}
                    className={errors.status ? 'is-invalid' : ''}
                  >
                    <option value=''>Choose Status..</option>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                  {errors.status && (
                    <div
                      className='invalid-feedback d-block position-absolute'
                      style={{ top: '100%', zIndex: 1000 }}
                    >
                      {errors.status}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-4 position-relative'>
                  <Form.Label>Orange Timer (Mins)</Form.Label>
                  <Form.Control
                    type='text'
                    name='orange_timer'
                    value={formData.orange_timer}
                    onChange={handleInputChange}
                    placeholder='Please enter value in minutes'
                    className={errors.orange_timer ? 'is-invalid' : ''}
                  />
                  {errors.orange_timer && (
                    <div
                      className='invalid-feedback d-block position-absolute'
                      style={{ top: '100%', zIndex: 1000 }}
                    >
                      {errors.orange_timer}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Red Timer (Mins)</Form.Label>
                  <Form.Control
                    type='text'
                    name='red_timer'
                    value={formData.red_timer}
                    onChange={handleInputChange}
                    placeholder='Please enter value in minutes'
                    isInvalid={!!errors.red_timer}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.red_timer}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className='mb-3'>
                  <Form.Label>Categories</Form.Label>
                  <div className='d-flex flex-wrap gap-3'>
                    {categoriesLoading ? (
                      <div className='text-center text-muted w-100'>
                        <small>Loading categories...</small>
                      </div>
                    ) : availableCategories.length > 0 ? (
                      availableCategories.map((category) => (
                        <Form.Check
                          key={category.id}
                          type='checkbox'
                          id={`category-${category.id}`}
                          label={category.name}
                          checked={
                            Array.isArray(formData.categories) &&
                            formData.categories.includes(category.id)
                          }
                          onChange={() => handleCategoryChange(category.id)}
                          className='me-3'
                        />
                      ))
                    ) : (
                      <div className='text-center text-muted w-100'>
                        <small>No categories available</small>
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className='d-flex justify-content-end gap-2'>
              <Button
                variant='outline-secondary'
                onClick={() =>
                  history.push(`/${lng}/settings/kitchen-displays`)
                }
                disabled={loading}
              >
                Cancel
              </Button>
              <Button variant='primary' type='submit' disabled={loading}>
                {loading ? (
                  <>
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                      className='me-2'
                    />
                    Updating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className='me-2' />
                    Save
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  )
}

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType
  }
}

export default connect(mapStateToProps)(withNamespaces()(EditKitchenDisplay))
